import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { HttpError, HttpService } from 'src/toolkit';
import { TTableSort, TableSort } from 'src/components/table/thead/col';

import { useAppSelector } from '../../hooks';
import { UserClientContactListSortCol, TUserClientContactList, TUserClientContactListResponse, TUserClientContactListQuery, TUserClientContactQueryPayload, TUserClientContactListSortCol, adaptUserClientContactFromListResponse, adaptUserClientContactQueryToRequest } from '../../types';
import { BACKEND_USER_CLIENT } from '../../backend-paths';


export const userClientContactListGetApi = createAsyncThunk(
	`GET/${BACKEND_USER_CLIENT}/:client_id/contacts`,
	(payload: TUserClientContactQueryPayload, thunkApi) => {
		const { clientId, data } = payload;
		const path = `${BACKEND_USER_CLIENT}/${clientId}/contacts`;

		const params = data ? adaptUserClientContactQueryToRequest(data) : {};

		return HttpService.get<TUserClientContactListResponse>(path, { params }).then(result => {
			return adaptUserClientContactFromListResponse(result.data);
		}).catch((error: HttpError) => {
			return thunkApi.rejectWithValue({ ...error });
		});
	},
);

type TInitialState = {
	isLoading: boolean,
	list: TUserClientContactList,
	query: TUserClientContactListQuery,
	error?: HttpError,
};

const initialState: TInitialState = {
	isLoading: false,
	list: {
		items: [],
		count: 0,
	},
	query: {
		limit: 10,
		offset: 0,
		search: undefined,
		sort: TableSort.ASC,
		sortCol: UserClientContactListSortCol.CREATED_AT,
	},
	error: undefined,
};

export const userClientContactListSlice = createSlice({
	name: 'userClientContactListSlice',
	initialState,
	reducers: {
		setUserClientContactListQueryLimit(state, action: PayloadAction<number>) {
			state.query.limit = action.payload;
		},

		setUserClientContactListQueryOffset(state, action: PayloadAction<number>) {
			state.query.offset = action.payload;
		},

		setUserClientContactListQuerySearch(state, action: PayloadAction<string>) {
			const search = action.payload || undefined;
			const isSearch = search && search.length > 0;

			if (isSearch && state.query.offset !== 0) {
				state.query.offset = 0;
				return;
			}

			state.query.search = search;
		},

		setUserClientContactListQuerySort(state, action: PayloadAction<TTableSort>) {
			state.query.sort = action.payload;
		},

		setUserClientContactListQuerySortCol(state, action: PayloadAction<TUserClientContactListSortCol>) {
			state.query.sortCol = action.payload;
		},

		clearUserClientContactList(state) {
			return { ...initialState, list: state.list, query: state.query };
		},
	},

	extraReducers: {
		[userClientContactListGetApi.pending.type]: (state) => {
			state.isLoading = true;
			state.error = undefined;
		},

		[userClientContactListGetApi.fulfilled.type]: (state, action: PayloadAction<TUserClientContactList>) => {
			state.isLoading = false;
			state.list = action.payload;
			state.error = undefined;
		},

		[userClientContactListGetApi.rejected.type]: (state, action: PayloadAction<HttpError>) => {
			state.isLoading = false;
			state.error = action.payload;
		},
	},
});

export const useUserClientContactListSelector = () => useAppSelector(state => state[userClientContactListSlice.name]);

export const { clearUserClientContactList, setUserClientContactListQueryLimit, setUserClientContactListQueryOffset, setUserClientContactListQuerySort, setUserClientContactListQuerySortCol, setUserClientContactListQuerySearch } = userClientContactListSlice.actions;
